import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from "@angular/common/http";
import { LOCALE_ID, enableProdMode, importProvidersFrom } from "@angular/core";
import { bootstrapApplication } from "@angular/platform-browser";
import {
  BrowserAnimationsModule,
  provideAnimations,
} from "@angular/platform-browser/animations";
import { provideRouter } from "@angular/router";
import { JwtModule } from "@auth0/angular-jwt";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { NgxsReduxDevtoolsPluginModule } from "@ngxs/devtools-plugin";
import { NgxsRouterPluginModule } from "@ngxs/router-plugin";
import { provideStore } from "@ngxs/store";
import { ModalModule } from "ngx-bootstrap/modal";
import { TabsModule } from "ngx-bootstrap/tabs";
import { NgxPageScrollCoreModule } from "ngx-page-scroll-core";
import { ToastrModule } from "ngx-toastr";
import { AppRoutingModule, routes } from "./app/app-routing.module";
import { AppComponent } from "./app/app.component";
import { EnvServiceProvider } from "./app/env.service.provider";
import { AuthGuard } from "./app/shared/guards/auth.guard";
import { CitoModuleGuard } from "./app/shared/guards/citomodule.guard";
import { TeacherAuthGuard } from "./app/shared/guards/teacher.guard";
import { BackendUrlInterceptor } from "./app/shared/interceptors/backed-url.interceptor";
import { ErrorInterceptor } from "./app/shared/interceptors/error.interceptor";
import { RouteInterceptor } from "./app/shared/interceptors/route.interceptor.ts";
import { AssessmentResultService } from "./app/shared/services/assessment-result.service";
import { AssignmentService } from "./app/shared/services/assignment.service";
import { CandidateProgressService } from "./app/shared/services/candidateprogress.service";
import { DomainService } from "./app/shared/services/domain.service";
import { GroupService } from "./app/shared/services/group.service";
import { LearningObjectiveOrderService } from "./app/shared/services/learning-objective-order.service";
import { LearningObjectiveService } from "./app/shared/services/learning-objective.service";
import { PlanningService } from "./app/shared/services/planning.service";
import { SchoolService } from "./app/shared/services/school.service";
import { UserService } from "./app/shared/services/user.service";
import { NgxsActionLoggerToBackendPluginModule } from "./app/shared/store/action-logger";
import { AssignmentDetailsState } from "./app/shared/store/assignment-details.state";
import { CandidateProgressState } from "./app/shared/store/candidate-progress.state";
import { DashboardState } from "./app/shared/store/dashboard.state";
import { LearningObjectiveInfoState } from "./app/shared/store/learning-objective-info.state";
import { LearningObjectiveOrderState } from "./app/shared/store/learning-objective-order.state";
import { LearningObjectivesState } from "./app/shared/store/learning-objectives.state";
import { PlanningFilterState } from "./app/shared/store/planning-filter.state";
import { PlanningState } from "./app/shared/store/planning.state";
import { environment } from "./environments/environment";

import { registerLocaleData } from "@angular/common";
import localeNL from "@angular/common/locales/nl";
registerLocaleData(localeNL);

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}
export function GetToken() {
  return sessionStorage.getItem(environment.jwt);
}

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    // NGXS Store must be initialized first
    provideStore([
      AssignmentDetailsState,
      LearningObjectiveInfoState,
      LearningObjectivesState,
      CandidateProgressState,
      PlanningState,
      PlanningFilterState,
      LearningObjectiveOrderState,
      DashboardState,
    ]),
    // Then NGXS plugins and other modules
    importProvidersFrom(
      AppRoutingModule,
      BrowserAnimationsModule,
      ToastrModule.forRoot(),
      ModalModule.forRoot(),
      TabsModule.forRoot(),
      NgxPageScrollCoreModule.forRoot(),
      NgxsReduxDevtoolsPluginModule.forRoot(),
      NgxsActionLoggerToBackendPluginModule.forRoot(),
      NgxsRouterPluginModule.forRoot(),
      JwtModule.forRoot({
        config: {
          tokenGetter: GetToken,
          allowedDomains: [
            "localhost:5000",
            "localhost:8081",
            "localhost:8080",
          ],
        },
      }),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      })
    ),
    provideHttpClient(withInterceptorsFromDi()),
    provideRouter(routes),
    provideAnimations(),
    // HTTP Interceptors
    { provide: HTTP_INTERCEPTORS, useClass: RouteInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BackendUrlInterceptor,
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    // Locale
    { provide: LOCALE_ID, useValue: "nl-NL" },
    // Services from SharedModule.forRoot()
    UserService,
    LearningObjectiveService,
    LearningObjectiveOrderService,
    AssessmentResultService,
    // Services from PlanningModule
    PlanningService,
    GroupService,
    AssignmentService,
    SchoolService,
    CandidateProgressService,
    // Services from OrderLearningObjectivesModule
    DomainService,
    // Guards
    AuthGuard,
    TeacherAuthGuard,
    CitoModuleGuard,
    // Environment service provider
    EnvServiceProvider,
  ],
}).catch((err) => console.error(err));
